/**
 * What a detail page asks the API for, per URL shape (web plan W0.16). One
 * definition for the page's hooks (`useDetailQuery` in @/hooks/useAppData) and
 * the cards' hover prefetch, so a prefetch warms exactly the queries and
 * variables the page it links to will run.
 *
 *   /brand/{id}, /pk/en/brand/{id}        brandById(id)
 *   /brand/{slug}, /pk/en/brand/{slug}    resolveEntityPath(BRAND, slug[, "pk-en"]),
 *                                         then brandById(the id it answers)
 *
 * Same for shop, store and promotion. The locale is the route's launched
 * `/{cc}/{lang}` prefix, and is left out on an un-prefixed route (the backend
 * then answers for wherever the entity lives).
 */
import type { DocumentNode } from 'graphql';
import type { OperationVariables } from '@apollo/client';
import { countryCode, localeKey, type Locale } from '@/seo/locales';
import { entityPath, splitLocalePath, type EntityRef, type EntityType } from '@/seo/urls';
import { isObjectId } from '@/utils/detailStatus';
import {
  BRAND_BY_ID,
  BRAND_PAGE_HUB,
  PROMOTION_BY_ID,
  RESOLVE_ENTITY_PATH,
  SHOP_BY_ID,
  STORE_BY_ID,
} from './queries';

/** Each type's detail query and its root field. */
export const ENTITY_DETAIL_QUERIES: Readonly<
  Record<EntityType, { query: DocumentNode; field: string }>
> = {
  brand: { query: BRAND_BY_ID, field: 'brandById' },
  shop: { query: SHOP_BY_ID, field: 'shopById' },
  store: { query: STORE_BY_ID, field: 'storeById' },
  promotion: { query: PROMOTION_BY_ID, field: 'promotionById' },
};

/** The backend's `EntityType` enum value for a route type. */
export const GRAPHQL_ENTITY_TYPES: Readonly<Record<EntityType, string>> = {
  brand: 'BRAND',
  shop: 'SHOP',
  store: 'STORE',
  promotion: 'PROMOTION',
};

/**
 * Variables of a `*ById` detail query: the id and, on a locale route
 * (`/bh/en/brand/…`), that route's country (`BH`), so the entity's stores,
 * promotions and `urlPath` are that country's. An un-prefixed legacy id URL
 * sends none (the entity's own country decides).
 */
export function detailVariables(id: string, locale?: Locale | null): { id: string; country?: string } {
  return locale ? { id, country: countryCode(locale) } : { id };
}

/** Variables of `resolveEntityPath` for a route's key and (optional) locale prefix. */
export function resolveVariables(
  type: EntityType,
  key: string,
  locale?: Locale | null,
): OperationVariables {
  const variables: OperationVariables = { type: GRAPHQL_ENTITY_TYPES[type], key };
  if (locale) variables.locale = localeKey(locale);
  return variables;
}

/** A detail route: its type, its `:ref` (an id or a slug, decoded) and its locale prefix. */
export interface EntityRoute {
  type: EntityType;
  ref: string;
  locale: Locale | null;
}

const ENTITY_ROUTE_RE = /^\/(brand|shop|store|promotion)\/([^/]+)\/?$/i;

/**
 * The detail route a site path names (`/brand/{ref}`, `/pk/en/brand/{ref}`,
 * any case, query and fragment ignored), as the router would match it; `null`
 * for any other path.
 */
export function parseEntityRoute(path: string): EntityRoute | null {
  const pathname = path.split(/[?#]/, 1)[0];
  const split = splitLocalePath(pathname);
  const match = ENTITY_ROUTE_RE.exec(split ? split.path : pathname);
  if (!match) return null;
  let ref: string;
  try {
    ref = decodeURIComponent(match[2]);
  } catch {
    return null;
  }
  return { type: match[1].toLowerCase() as EntityType, ref, locale: split?.locale ?? null };
}

/** One query a prefetch runs: the document and its exact variables. */
export interface PrefetchRequest {
  query: DocumentNode;
  variables: OperationVariables;
}

/**
 * The queries the detail page behind `entityPath(entity, type)` runs for
 * `entity`: its `*ById` query for an id path; for a slug path the resolution,
 * then the `*ById` query with the id it answers (the entity's own).
 *
 * For a brand slug path (web plan W3.6), the brand hub page also fires
 * `BrandPageHub` in parallel with the entity query once the entity loads
 * successfully, so the prefetch includes it here too.
 */
export function entityPageRequests(type: EntityType, entity: EntityRef): PrefetchRequest[] {
  const route = parseEntityRoute(entityPath(entity, type));
  const routeType = route?.type ?? type;
  const byId = (id: string): PrefetchRequest => ({
    query: ENTITY_DETAIL_QUERIES[routeType].query,
    variables: detailVariables(id, route?.locale),
  });
  if (!route || isObjectId(route.ref)) return [byId(route?.ref ?? entity.id)];
  const base: PrefetchRequest[] = [
    { query: RESOLVE_ENTITY_PATH, variables: resolveVariables(routeType, route.ref, route.locale) },
    byId(entity.id),
  ];
  // W3.6: brand hub pages also fire BrandPageHub (slug paths only — the page
  // skips it for ObjectId refs, which are handled above with early return).
  // The hook always passes a locale (defaulting to DEFAULT_LOCALE when the
  // route has no prefix), so the prefetch must match that exact string.
  if (routeType === 'brand') {
    base.push({
      query: BRAND_PAGE_HUB,
      variables: { slug: route.ref, locale: localeKey(route.locale ?? undefined) },
    });
  }
  return base;
}
